import type StoreService from '@ember-data/store';
import type DeskModel from 'garaje/models/desk';
import type MapFeatureModel from 'garaje/models/map-feature';
import type { Resource } from 'garaje/types/maps/live-map/LiveMapServiceV2';

type Point = [number, number];
type PolygonArray = Array<Point>;
export type NeighborhoodPolygons = {
  [neighborhoodId: string]: PolygonArray[];
};

interface NeighborhoodToDesksMap {
  [neighborhoodId: string]: DeskModel[];
}

// Constants for neighborhood polygon calculations
const MIN_SLIDER = 0.0075;
const MAX_SLIDER = 0.045;
const MIN_THRESHOLD = 0.002;
const MAX_THRESHOLD = 0.3;
const MIN_MARGIN = 0.0105; // Increased for better visibility in live map
const MAX_MARGIN = 0.1;

function getMargins(
  mapWidth: number,
  mapHeight: number,
  marginLength: number,
): { XMARGIN: number; YMARGIN: number; normalizingFactor: number } {
  const normalizingFactor = mapWidth > mapHeight ? mapWidth / mapHeight : mapHeight / mapWidth;
  const XMARGIN = mapWidth > mapHeight ? marginLength / normalizingFactor : marginLength;
  const YMARGIN = mapHeight > mapWidth ? marginLength / normalizingFactor : marginLength;
  return { XMARGIN, YMARGIN, normalizingFactor };
}

export function getNeighborhoodPolygons(
  deskFeatures: MapFeatureModel[],
  mapDimensions: number[],
  store: StoreService,
  neighborhoodMarginSetting: number,
  activeNeighborhoodIdMap?: Record<string, string>,
): NeighborhoodPolygons {
  const neighborhoodToDesksMap: NeighborhoodToDesksMap = {};
  const desksOnFloor = deskFeatures.map((deskFeature: MapFeatureModel) => {
    if (deskFeature.externalId) {
      return store.peekRecord('desk', String(deskFeature.externalId));
    } else {
      return deskFeature.desk;
    }
  });
  desksOnFloor.forEach((desk) => {
    if (desk) {
      const neighborhoodId: string = activeNeighborhoodIdMap?.[desk.id] ?? desk.belongsTo('neighborhood').id();
      if (neighborhoodId) {
        if (neighborhoodToDesksMap[neighborhoodId]) {
          neighborhoodToDesksMap[neighborhoodId].push(desk);
        } else {
          neighborhoodToDesksMap[neighborhoodId] = [desk];
        }
      }
    }
  });
  const [mapWidth, mapHeight] = mapDimensions;

  // Calculate clustering threshold using squared relationship (matches workplace)
  const normalized = (neighborhoodMarginSetting - MIN_SLIDER) / (MAX_SLIDER - MIN_SLIDER);
  const clusteringThreshold = MIN_THRESHOLD + normalized * normalized * (MAX_THRESHOLD - MIN_THRESHOLD);

  // Calculate visual margin using cubic relationship (matches workplace)
  let visualMargin = MIN_MARGIN + normalized * normalized * normalized * (MAX_MARGIN - MIN_MARGIN);

  // Clamp to prevent diagonal overlaps, but ensure minimum visibility
  const maxAllowedMargin = Math.max(clusteringThreshold * 0.3, MIN_MARGIN);
  visualMargin = Math.min(visualMargin, maxAllowedMargin);

  const { XMARGIN, YMARGIN, normalizingFactor } = getMargins(mapWidth!, mapHeight!, visualMargin);
  const isWideFloor = mapWidth! > mapHeight!;
  const delta = (visualMargin * 0.2) / normalizingFactor;

  const polygonCoords: NeighborhoodPolygons = {};
  Object.keys(neighborhoodToDesksMap).map((neighborhoodId) => {
    const desks: DeskModel[] = neighborhoodToDesksMap[neighborhoodId]!;
    const deskClusters = getDeskClusters(desks, clusteringThreshold, isWideFloor, normalizingFactor);

    deskClusters.forEach((cluster) => {
      const exteriorDesks = findExteriorDesks(cluster);
      const exteriorIntersections = findExteriorIntersections(exteriorDesks);
      const [topLeft, topRight, bottomRight, bottomLeft] = exteriorIntersections;

      // Disable folding for small margins to prevent self-intersecting geometry
      // When margin is less than 0.01 (1% of image), use simple bounding box
      // This matches workplace behavior
      const minMargin = Math.min(XMARGIN, YMARGIN);
      let tightPolygon: PolygonArray;

      if (minMargin < 0.01) {
        // Return simple rectangle
        tightPolygon = [topLeft!, topRight!, bottomRight!, bottomLeft!, topLeft!];
      } else {
        // Use corner folding for larger margins
        const topRightFoldedPoints = foldTopRightCorner(exteriorIntersections, cluster);
        const topRightPoints = topRightFoldedPoints.length ? topRightFoldedPoints : [topRight];

        const bottomRightFoldedPoints = foldBottomRightCorner(exteriorIntersections, cluster);
        const bottomRightPoints = bottomRightFoldedPoints.length ? bottomRightFoldedPoints : [bottomRight];

        const topLeftFoldedPoints = foldTopLeftCorner(exteriorIntersections, cluster);
        const topLeftPoints = topLeftFoldedPoints.length ? topLeftFoldedPoints : [topLeft];

        const bottomLeftFoldedPoints = foldBottomLeftCorner(exteriorIntersections, cluster);
        const bottomLeftPoints = bottomLeftFoldedPoints.length ? bottomLeftFoldedPoints : [bottomLeft];

        tightPolygon = [...topLeftPoints, ...topRightPoints, ...bottomRightPoints, ...bottomLeftPoints] as PolygonArray;
      }
      if (polygonCoords[neighborhoodId]) {
        polygonCoords[neighborhoodId].push(tightPolygon);
      } else {
        polygonCoords[neighborhoodId] = [tightPolygon];
      }
    });
  });
  return polygonCoords;

  function foldTopRightCorner(bounds: PolygonArray, cluster: DeskModel[]): PolygonArray {
    const [_topLeft, topRight, _bottomRight, _bottomLeft] = bounds;

    if (!topRight) {
      return [];
    }

    let isDeskInHorizontalBound = false;
    let isDeskInVerticalBound = false;
    let checkingWidth = topRight[0];
    let checkingHeight = topRight[1];
    let foldPoints: PolygonArray = [
      [checkingWidth, topRight[1]],
      [checkingWidth, checkingHeight],
      [topRight[0], checkingHeight],
    ];

    for (let i = 1; ; i++) {
      if (!isDeskInHorizontalBound) {
        checkingWidth = topRight[0] - delta * i;
      }

      if (!isDeskInVerticalBound) {
        checkingHeight = topRight[1] + delta * i;
      }

      if (checkingWidth < 0 || checkingHeight > 1) {
        return [];
      }

      // Check if desk is in bounds
      if (!isDeskInHorizontalBound) {
        isDeskInHorizontalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const horizontalCheckingBounds: PolygonArray = [topRight, [checkingWidth, checkingHeight - delta]];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isDeskInHorizontalBound) {
          checkingWidth = checkingWidth + delta;
        }
      }

      if (!isDeskInVerticalBound) {
        isDeskInVerticalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const verticalCheckingBounds: PolygonArray = [topRight, [checkingWidth + delta, checkingHeight]];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isDeskInVerticalBound) {
          checkingHeight = checkingHeight - delta;
        }
      }

      if (isDeskInHorizontalBound && isDeskInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [checkingWidth, topRight[1]],
          [checkingWidth, checkingHeight],
          [topRight[0], checkingHeight],
        ];
      }
    }
  }

  function foldBottomRightCorner(bounds: PolygonArray, cluster: DeskModel[]): PolygonArray {
    const [_topLeft, _topRight, bottomRight, _bottomLeft] = bounds;

    if (!bottomRight) {
      return [];
    }

    let isDeskInHorizontalBound = false;
    let isDeskInVerticalBound = false;
    let checkingWidth = bottomRight[0];
    let checkingHeight = bottomRight[1];
    let foldPoints: PolygonArray = [
      [bottomRight[0], checkingHeight],
      [checkingWidth, checkingHeight],
      [checkingWidth, bottomRight[1]],
    ];

    for (let i = 1; ; i++) {
      if (!isDeskInHorizontalBound) {
        checkingWidth = bottomRight[0] - delta * i;
      }

      if (!isDeskInVerticalBound) {
        checkingHeight = bottomRight[1] - delta * i;
      }

      if (checkingWidth < 0 || checkingHeight < 0) {
        return [];
      }

      // Check if desk is in bounds
      if (!isDeskInHorizontalBound) {
        isDeskInHorizontalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const horizontalCheckingBounds: PolygonArray = [
            [bottomRight[0], checkingHeight + delta],
            [checkingWidth, bottomRight[1]],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isDeskInHorizontalBound) {
          checkingWidth = checkingWidth + delta;
        }
      }

      if (!isDeskInVerticalBound) {
        isDeskInVerticalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const verticalCheckingBounds: PolygonArray = [
            [bottomRight[0], checkingHeight],
            [checkingWidth + delta, bottomRight[1]],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isDeskInVerticalBound) {
          checkingHeight = checkingHeight + delta;
        }
      }

      if (isDeskInHorizontalBound && isDeskInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [bottomRight[0], checkingHeight],
          [checkingWidth, checkingHeight],
          [checkingWidth, bottomRight[1]],
        ];
      }
    }
  }

  function foldTopLeftCorner(bounds: PolygonArray, cluster: DeskModel[]): PolygonArray {
    const [topLeft, _topRight, _bottomRight, _bottomLeft] = bounds;

    if (!topLeft) {
      return [];
    }

    let isDeskInHorizontalBound = false;
    let isDeskInVerticalBound = false;
    let checkingWidth = topLeft[0];
    let checkingHeight = topLeft[1];
    let foldPoints: PolygonArray = [
      [topLeft[0], checkingHeight],
      [checkingWidth, checkingHeight],
      [checkingWidth, topLeft[1]],
    ];

    for (let i = 1; ; i++) {
      if (!isDeskInHorizontalBound) {
        checkingWidth = topLeft[0] + delta * i;
      }

      if (!isDeskInVerticalBound) {
        checkingHeight = topLeft[1] + delta * i;
      }

      if (checkingWidth > 1 || checkingHeight > 1) {
        return [];
      }

      // Check if desk is in bounds
      if (!isDeskInHorizontalBound) {
        isDeskInHorizontalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const horizontalCheckingBounds: PolygonArray = [
            [checkingWidth, topLeft[1]],
            [topLeft[0], checkingHeight - delta],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isDeskInHorizontalBound) {
          checkingWidth = checkingWidth - delta;
        }
      }

      if (!isDeskInVerticalBound) {
        isDeskInVerticalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const verticalCheckingBounds: PolygonArray = [
            [checkingWidth - delta, topLeft[1]],
            [topLeft[0], checkingHeight],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isDeskInVerticalBound) {
          checkingHeight = checkingHeight - delta;
        }
      }

      if (isDeskInHorizontalBound && isDeskInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [topLeft[0], checkingHeight],
          [checkingWidth, checkingHeight],
          [checkingWidth, topLeft[1]],
        ];
      }
    }
  }

  function foldBottomLeftCorner(bounds: PolygonArray, cluster: DeskModel[]): PolygonArray {
    const [_topLeft, _topRight, _bottomRight, bottomLeft] = bounds;

    if (!bottomLeft) {
      return [];
    }

    let isDeskInHorizontalBound = false;
    let isDeskInVerticalBound = false;
    let checkingWidth = bottomLeft[0];
    let checkingHeight = bottomLeft[1];
    let foldPoints: PolygonArray = [
      [checkingWidth, bottomLeft[1]],
      [checkingWidth, checkingHeight],
      [bottomLeft[0], checkingHeight],
    ];

    for (let i = 1; ; i++) {
      if (!isDeskInHorizontalBound) {
        checkingWidth = bottomLeft[0] + delta * i;
      }

      if (!isDeskInVerticalBound) {
        checkingHeight = bottomLeft[1] - delta * i;
      }

      if (checkingWidth > 1 || checkingHeight < 0) {
        return [];
      }

      // Check if desk is in bounds
      if (!isDeskInHorizontalBound) {
        isDeskInHorizontalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const horizontalCheckingBounds: PolygonArray = [
            [checkingWidth, checkingHeight + delta],
            [bottomLeft[0], bottomLeft[1]],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isDeskInHorizontalBound) {
          checkingWidth = checkingWidth - delta;
        }
      }

      if (!isDeskInVerticalBound) {
        isDeskInVerticalBound = cluster.some((desk) => {
          const point: Point = [desk.xPos, desk.yPos];
          const verticalCheckingBounds: PolygonArray = [
            [checkingWidth - delta, checkingHeight],
            [bottomLeft[0], bottomLeft[1]],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isDeskInVerticalBound) {
          checkingHeight = checkingHeight + delta;
        }
      }

      if (isDeskInHorizontalBound && isDeskInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [checkingWidth, bottomLeft[1]],
          [checkingWidth, checkingHeight],
          [bottomLeft[0], checkingHeight],
        ];
      }
    }
  }

  function isPointInBoxHorizontal(point: Point, box: PolygonArray): boolean {
    // Extract the bounding box corners (assuming it is axis-aligned)
    const [topRight, bottomLeft] = box;

    const pointIsInWidth = point[0] + XMARGIN >= bottomLeft![0] && point[0] - XMARGIN <= topRight![0];
    const pointIsInHeight = point[1] - YMARGIN <= bottomLeft![1] && point[1] + YMARGIN >= topRight![1];
    // Check if the point lies within the bounds
    const pointIsInBox = pointIsInWidth && pointIsInHeight;

    if (pointIsInBox && pointIsInWidth) {
      return true;
    }
    return false;
  }

  function isPointInBoxVertical(point: Point, box: PolygonArray): boolean {
    // Extract the bounding box corners (assuming it is axis-aligned)
    const [topRight, bottomLeft] = box;
    // Check if the point lies within the bounds
    const pointIsInWidth = point[0] + XMARGIN >= bottomLeft![0] && point[0] - XMARGIN <= topRight![0];
    const pointIsInHeight = point[1] - YMARGIN <= bottomLeft![1] && point[1] + YMARGIN >= topRight![1];
    // Check if the point lies within the bounds
    const pointIsInBox = pointIsInWidth && pointIsInHeight;
    if (pointIsInBox && pointIsInHeight) {
      return true;
    }
    return false;
  }

  function findExteriorDesks(desks: DeskModel[]): DeskModel[] {
    let leftMost = desks[0]!;
    let rightMost = desks[0]!;
    let topMost = desks[0]!;
    let bottomMost = desks[0]!;

    desks.forEach((desk) => {
      if (desk.xPos < leftMost.xPos) {
        leftMost = desk;
      }
      if (desk.xPos > rightMost.xPos) {
        rightMost = desk;
      }
      if (desk.yPos < topMost.yPos) {
        topMost = desk;
      }
      if (desk.yPos > bottomMost.yPos) {
        bottomMost = desk;
      }
    });
    return [leftMost, topMost, rightMost, bottomMost];
  }

  function findExteriorIntersections(exteriorDesks: DeskModel[]): PolygonArray {
    // Find the intersection of the lines that connect the exterior desks
    const [leftMost, topMost, rightMost, bottomMost] = exteriorDesks;
    const topLeft: Point = [leftMost!.xPos - XMARGIN, topMost!.yPos - YMARGIN];
    const topRight: Point = [rightMost!.xPos + XMARGIN, topMost!.yPos - YMARGIN];
    const bottomLeft: Point = [leftMost!.xPos - XMARGIN, bottomMost!.yPos + YMARGIN];
    const bottomRight: Point = [rightMost!.xPos + XMARGIN, bottomMost!.yPos + YMARGIN];

    return [topLeft, topRight, bottomRight, bottomLeft];
  }

  function getDeskClusters(
    desks: DeskModel[],
    clusteringThreshold: number,
    isWideFloor: boolean,
    normalizingFactor: number,
  ) {
    const deskStack: DeskModel[] = [];
    const visited: { [deskId: string]: boolean } = {};
    const clusters: DeskModel[][] = [];
    const mergeThreshold = clusteringThreshold;

    desks.forEach((desk) => {
      if (visited[desk.id]) return;
      deskStack.push(desk);
      let cluster: DeskModel[] = [];
      while (deskStack.length) {
        const currentDesk = deskStack.pop();
        if (visited[currentDesk!.id]) continue;
        visited[currentDesk!.id] = true;
        const neighbors = getNeighbors(currentDesk!, desks, mergeThreshold, isWideFloor, normalizingFactor);
        cluster.push(currentDesk!);
        deskStack.push(...neighbors);
      }
      clusters.push(cluster);
      cluster = [];
    });
    return clusters;
  }

  function getNeighbors(
    desk: DeskModel,
    desks: DeskModel[],
    mergeThreshold: number,
    isWideFloor: boolean,
    normalizingFactor: number,
  ) {
    const neighbors: DeskModel[] = [];
    desks.forEach((neighbor) => {
      if (neighbor.id === desk.id) return;
      const distance = getDistance(desk, neighbor, isWideFloor, normalizingFactor);
      if (distance < mergeThreshold) {
        neighbors.push(neighbor);
      }
    });
    return neighbors;
  }

  function getDistance(desk1: DeskModel, desk2: DeskModel, isWideFloor: boolean, normalizingFactor: number): number {
    let width = Math.abs(desk1.xPos - desk2.xPos);
    let height = Math.abs(desk1.yPos - desk2.yPos);
    width = isWideFloor ? width * normalizingFactor : width;
    height = isWideFloor ? height : height * normalizingFactor;
    return Math.hypot(width, height);
  }
}

// New function that works with the Resource type from LiveMapServiceV2
export function getNeighborhoodPolygonsFromResources(
  resources: Resource[],
  mapDimensions: number[],
  neighborhoodMarginSetting: number = 0.0265,
): NeighborhoodPolygons {
  function isPointInBoxHorizontal(point: Point, box: PolygonArray): boolean {
    if (box.length < 2) return false;

    const [topRight, bottomLeft] = box;
    if (!topRight || !bottomLeft) return false;

    const pointIsInWidth = point[0] + XMARGIN >= bottomLeft[0] && point[0] - XMARGIN <= topRight[0];
    const pointIsInHeight = point[1] - YMARGIN <= bottomLeft[1] && point[1] + YMARGIN >= topRight[1];
    const pointIsInBox = pointIsInWidth && pointIsInHeight;

    return pointIsInBox && pointIsInWidth;
  }

  function isPointInBoxVertical(point: Point, box: PolygonArray): boolean {
    if (box.length < 2) return false;

    const [topRight, bottomLeft] = box;
    if (!topRight || !bottomLeft) return false;

    const pointIsInWidth = point[0] + XMARGIN >= bottomLeft[0] && point[0] - XMARGIN <= topRight[0];
    const pointIsInHeight = point[1] - YMARGIN <= bottomLeft[1] && point[1] + YMARGIN >= topRight[1];
    const pointIsInBox = pointIsInWidth && pointIsInHeight;

    return pointIsInBox && pointIsInHeight;
  }

  // Filter resources to get only desk resources
  const deskResources = resources.filter((resource) => resource.resourceTypeId === 'resource_type:desk');

  // Group desk resources by neighborhood
  const neighborhoodToResourcesMap: Record<string, Resource[]> = {};

  deskResources.forEach((resource) => {
    if (resource.neighborhoodId) {
      const resources = neighborhoodToResourcesMap[resource.neighborhoodId];
      if (resources) {
        resources.push(resource);
      } else {
        neighborhoodToResourcesMap[resource.neighborhoodId] = [resource];
      }
    }
  });

  const [mapWidth, mapHeight] = mapDimensions;
  if (!mapWidth || !mapHeight) {
    return {};
  }

  // Calculate clustering threshold using squared relationship (matches workplace)
  const normalized = (neighborhoodMarginSetting - MIN_SLIDER) / (MAX_SLIDER - MIN_SLIDER);
  const clusteringThreshold = MIN_THRESHOLD + normalized * normalized * (MAX_THRESHOLD - MIN_THRESHOLD);

  // Calculate visual margin using cubic relationship (matches workplace)
  let visualMargin = MIN_MARGIN + normalized * normalized * normalized * (MAX_MARGIN - MIN_MARGIN);

  // Clamp to prevent diagonal overlaps, but ensure minimum visibility
  const maxAllowedMargin = Math.max(clusteringThreshold * 0.3, MIN_MARGIN);
  visualMargin = Math.min(visualMargin, maxAllowedMargin);

  const { XMARGIN, YMARGIN, normalizingFactor } = getMargins(mapWidth, mapHeight, visualMargin);
  const isWideFloor = mapWidth > mapHeight;
  const delta = (visualMargin * 0.2) / normalizingFactor;
  const mergeThreshold = clusteringThreshold;

  const polygonCoords: NeighborhoodPolygons = {};

  Object.keys(neighborhoodToResourcesMap).forEach((neighborhoodId) => {
    const resources = neighborhoodToResourcesMap[neighborhoodId] || [];
    if (resources.length === 0) return;

    const resourceClusters = getResourceClusters(resources);

    resourceClusters.forEach((cluster) => {
      if (cluster.length === 0) return;

      const exteriorResources = findExteriorResources(cluster);
      if (exteriorResources.length < 4) return;

      const exteriorIntersections = findExteriorIntersections(exteriorResources);
      const [topLeft, topRight, bottomRight, bottomLeft] = exteriorIntersections;

      // Disable folding for small margins to prevent self-intersecting geometry
      // When margin is less than 0.02, use simple bounding box to avoid complex geometry issues
      const minMargin = Math.min(XMARGIN, YMARGIN);
      let tightPolygon: PolygonArray;

      if (minMargin < 0.02) {
        // Return simple rectangle
        tightPolygon = [topLeft!, topRight!, bottomRight!, bottomLeft!, topLeft!];
      } else {
        // Use corner folding for larger margins
        const topRightFoldedPoints = foldTopRightCorner(exteriorIntersections, cluster);
        const topRightPoints = topRightFoldedPoints.length ? topRightFoldedPoints : [topRight];

        const bottomRightFoldedPoints = foldBottomRightCorner(exteriorIntersections, cluster);
        const bottomRightPoints = bottomRightFoldedPoints.length ? bottomRightFoldedPoints : [bottomRight];

        const topLeftFoldedPoints = foldTopLeftCorner(exteriorIntersections, cluster);
        const topLeftPoints = topLeftFoldedPoints.length ? topLeftFoldedPoints : [topLeft];

        const bottomLeftFoldedPoints = foldBottomLeftCorner(exteriorIntersections, cluster);
        const bottomLeftPoints = bottomLeftFoldedPoints.length ? bottomLeftFoldedPoints : [bottomLeft];

        tightPolygon = [...topLeftPoints, ...topRightPoints, ...bottomRightPoints, ...bottomLeftPoints] as PolygonArray;
      }

      if (polygonCoords[neighborhoodId]) {
        polygonCoords[neighborhoodId].push(tightPolygon);
      } else {
        polygonCoords[neighborhoodId] = [tightPolygon];
      }
    });
  });

  return polygonCoords;

  function getResourceClusters(resources: Resource[]): Resource[][] {
    if (resources.length === 0) {
      return [];
    }

    const resourceStack: Resource[] = [];
    const visited: Record<string, boolean> = {};
    const clusters: Resource[][] = [];

    resources.forEach((resource) => {
      if (visited[resource.id]) return;

      resourceStack.push(resource);
      const cluster: Resource[] = [];

      while (resourceStack.length > 0) {
        const currentResource = resourceStack.pop();
        if (!currentResource || visited[currentResource.id]) continue;

        visited[currentResource.id] = true;
        const neighbors = getNeighbors(currentResource, resources);
        cluster.push(currentResource);
        resourceStack.push(...neighbors);
      }

      if (cluster.length > 0) {
        clusters.push(cluster);
      }
    });
    return clusters;
  }

  function getNeighbors(resource: Resource, resources: Resource[]): Resource[] {
    // First validate that the resource has valid coordinates
    const coords = resource.geometry.pointGeometry?.coordinates;
    if (!coords || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
      return [];
    }

    return resources.filter((neighbor) => {
      if (neighbor.id === resource.id) return false;
      const distance = getDistance(resource, neighbor);
      return distance < mergeThreshold;
    });
  }

  function getDistance(resource1: Resource, resource2: Resource): number {
    const coords1 = resource1.geometry.pointGeometry?.coordinates;
    const coords2 = resource2.geometry.pointGeometry?.coordinates;

    if (!coords1 || !coords2) return Infinity;

    const [x1, y1] = coords1;
    const [x2, y2] = coords2;

    // Validate coordinates are numbers
    if (typeof x1 !== 'number' || typeof y1 !== 'number' || typeof x2 !== 'number' || typeof y2 !== 'number') {
      return Infinity;
    }

    let width = Math.abs(x1 - x2);
    let height = Math.abs(y1 - y2);
    width = isWideFloor ? width * normalizingFactor : width;
    height = isWideFloor ? height : height * normalizingFactor;

    return Math.hypot(width, height);
  }

  function findExteriorResources(resources: Resource[]): Resource[] {
    if (resources.length === 0) return [];

    // Initialize with the first resource
    const firstResource = resources[0];
    if (!firstResource) return [];

    let leftMost = firstResource;
    let rightMost = firstResource;
    let topMost = firstResource;
    let bottomMost = firstResource;

    for (const resource of resources) {
      const coords = resource.geometry.pointGeometry?.coordinates;
      if (!coords) continue;

      const [x, y] = coords;
      const leftCoords = leftMost.geometry.pointGeometry?.coordinates;
      const rightCoords = rightMost.geometry.pointGeometry?.coordinates;
      const topCoords = topMost.geometry.pointGeometry?.coordinates;
      const bottomCoords = bottomMost.geometry.pointGeometry?.coordinates;

      if (!leftCoords || x < leftCoords[0]) leftMost = resource;
      if (!rightCoords || x > rightCoords[0]) rightMost = resource;
      if (!topCoords || y < topCoords[1]) topMost = resource;
      if (!bottomCoords || y > bottomCoords[1]) bottomMost = resource;
    }

    return [leftMost, topMost, rightMost, bottomMost];
  }

  function findExteriorIntersections(exteriorResources: Resource[]): PolygonArray {
    if (exteriorResources.length < 4) {
      return [
        [0, 0],
        [1, 0],
        [1, 1],
        [0, 1],
      ];
    }

    const [leftMost, topMost, rightMost, bottomMost] = exteriorResources;
    if (!leftMost || !topMost || !rightMost || !bottomMost) {
      return [
        [0, 0],
        [1, 0],
        [1, 1],
        [0, 1],
      ];
    }

    // Safe access to coordinates with fallbacks to 0
    const leftX = leftMost.geometry.pointGeometry?.coordinates?.[0] ?? 0;
    const topY = topMost.geometry.pointGeometry?.coordinates?.[1] ?? 0;
    const rightX = rightMost.geometry.pointGeometry?.coordinates?.[0] ?? 0;
    const bottomY = bottomMost.geometry.pointGeometry?.coordinates?.[1] ?? 0;

    const topLeft: Point = [leftX - XMARGIN, topY - YMARGIN];
    const topRight: Point = [rightX + XMARGIN, topY - YMARGIN];
    const bottomLeft: Point = [leftX - XMARGIN, bottomY + YMARGIN];
    const bottomRight: Point = [rightX + XMARGIN, bottomY + YMARGIN];

    return [topLeft, topRight, bottomRight, bottomLeft];
  }

  function foldTopRightCorner(bounds: PolygonArray, cluster: Resource[]): PolygonArray {
    if (!bounds || bounds.length < 4) {
      return [];
    }

    const topRight = bounds[1];
    if (!topRight) {
      return [];
    }

    let isResourceInHorizontalBound = false;
    let isResourceInVerticalBound = false;
    let checkingWidth = topRight[0];
    let checkingHeight = topRight[1];
    let foldPoints: PolygonArray = [
      [checkingWidth, topRight[1]],
      [checkingWidth, checkingHeight],
      [topRight[0], checkingHeight],
    ];

    for (let i = 1; i < 1000; i++) {
      if (!isResourceInHorizontalBound) {
        checkingWidth = topRight[0] - delta * i;
      }

      if (!isResourceInVerticalBound) {
        checkingHeight = topRight[1] + delta * i;
      }

      if (checkingWidth < 0 || checkingHeight > 1) {
        return [];
      }

      // Check if resource is in bounds
      if (!isResourceInHorizontalBound) {
        isResourceInHorizontalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const horizontalCheckingBounds: PolygonArray = [topRight, [checkingWidth, checkingHeight - delta]];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isResourceInHorizontalBound) {
          checkingWidth = checkingWidth + delta;
        }
      }

      if (!isResourceInVerticalBound) {
        isResourceInVerticalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const verticalCheckingBounds: PolygonArray = [topRight, [checkingWidth + delta, checkingHeight]];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isResourceInVerticalBound) {
          checkingHeight = checkingHeight - delta;
        }
      }

      if (isResourceInHorizontalBound && isResourceInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [checkingWidth, topRight[1]],
          [checkingWidth, checkingHeight],
          [topRight[0], checkingHeight],
        ];
      }
    }

    // This should never happen, but TypeScript needs a return here
    return [];
  }

  function foldBottomRightCorner(bounds: PolygonArray, cluster: Resource[]): PolygonArray {
    const [_topLeft, _topRight, bottomRight, _bottomLeft] = bounds;

    if (!bottomRight) {
      return [];
    }

    let isResourceInHorizontalBound = false;
    let isResourceInVerticalBound = false;
    let checkingWidth = bottomRight[0];
    let checkingHeight = bottomRight[1];
    let foldPoints: PolygonArray = [
      [bottomRight[0], checkingHeight],
      [checkingWidth, checkingHeight],
      [checkingWidth, bottomRight[1]],
    ];

    for (let i = 1; ; i++) {
      if (!isResourceInHorizontalBound) {
        checkingWidth = bottomRight[0] - delta * i;
      }

      if (!isResourceInVerticalBound) {
        checkingHeight = bottomRight[1] - delta * i;
      }

      if (checkingWidth < 0 || checkingHeight < 0) {
        return [];
      }

      // Check if resource is in bounds
      if (!isResourceInHorizontalBound) {
        isResourceInHorizontalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const horizontalCheckingBounds: PolygonArray = [
            [bottomRight[0], checkingHeight + delta],
            [checkingWidth, bottomRight[1]],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isResourceInHorizontalBound) {
          checkingWidth = checkingWidth + delta;
        }
      }

      if (!isResourceInVerticalBound) {
        isResourceInVerticalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const verticalCheckingBounds: PolygonArray = [
            [bottomRight[0], checkingHeight],
            [checkingWidth + delta, bottomRight[1]],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isResourceInVerticalBound) {
          checkingHeight = checkingHeight + delta;
        }
      }

      if (isResourceInHorizontalBound && isResourceInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [bottomRight[0], checkingHeight],
          [checkingWidth, checkingHeight],
          [checkingWidth, bottomRight[1]],
        ];
      }
    }
  }

  function foldTopLeftCorner(bounds: PolygonArray, cluster: Resource[]): PolygonArray {
    const [topLeft, _topRight, _bottomRight, _bottomLeft] = bounds;

    if (!topLeft) {
      return [];
    }

    let isResourceInHorizontalBound = false;
    let isResourceInVerticalBound = false;
    let checkingWidth = topLeft[0];
    let checkingHeight = topLeft[1];
    let foldPoints: PolygonArray = [
      [topLeft[0], checkingHeight],
      [checkingWidth, checkingHeight],
      [checkingWidth, topLeft[1]],
    ];

    for (let i = 1; ; i++) {
      if (!isResourceInHorizontalBound) {
        checkingWidth = topLeft[0] + delta * i;
      }

      if (!isResourceInVerticalBound) {
        checkingHeight = topLeft[1] + delta * i;
      }

      if (checkingWidth > 1 || checkingHeight > 1) {
        return [];
      }

      // Check if resource is in bounds
      if (!isResourceInHorizontalBound) {
        isResourceInHorizontalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const horizontalCheckingBounds: PolygonArray = [
            [checkingWidth, topLeft[1]],
            [topLeft[0], checkingHeight - delta],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isResourceInHorizontalBound) {
          checkingWidth = checkingWidth - delta;
        }
      }

      if (!isResourceInVerticalBound) {
        isResourceInVerticalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const verticalCheckingBounds: PolygonArray = [
            [checkingWidth - delta, topLeft[1]],
            [topLeft[0], checkingHeight],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isResourceInVerticalBound) {
          checkingHeight = checkingHeight - delta;
        }
      }

      if (isResourceInHorizontalBound && isResourceInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [topLeft[0], checkingHeight],
          [checkingWidth, checkingHeight],
          [checkingWidth, topLeft[1]],
        ];
      }
    }
  }

  function foldBottomLeftCorner(bounds: PolygonArray, cluster: Resource[]): PolygonArray {
    const [_topLeft, _topRight, _bottomRight, bottomLeft] = bounds;

    if (!bottomLeft) {
      return [];
    }

    let isResourceInHorizontalBound = false;
    let isResourceInVerticalBound = false;
    let checkingWidth = bottomLeft[0];
    let checkingHeight = bottomLeft[1];
    let foldPoints: PolygonArray = [
      [checkingWidth, bottomLeft[1]],
      [checkingWidth, checkingHeight],
      [bottomLeft[0], checkingHeight],
    ];

    for (let i = 1; ; i++) {
      if (!isResourceInHorizontalBound) {
        checkingWidth = bottomLeft[0] + delta * i;
      }

      if (!isResourceInVerticalBound) {
        checkingHeight = bottomLeft[1] - delta * i;
      }

      if (checkingWidth > 1 || checkingHeight < 0) {
        return [];
      }

      // Check if resource is in bounds
      if (!isResourceInHorizontalBound) {
        isResourceInHorizontalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const horizontalCheckingBounds: PolygonArray = [
            [checkingWidth, checkingHeight + delta],
            [bottomLeft[0], bottomLeft[1]],
          ];
          return isPointInBoxHorizontal(point, horizontalCheckingBounds);
        });

        if (isResourceInHorizontalBound) {
          checkingWidth = checkingWidth - delta;
        }
      }

      if (!isResourceInVerticalBound) {
        isResourceInVerticalBound = cluster.some((resource) => {
          const coords = resource.geometry.pointGeometry?.coordinates;
          if (!coords) return false;
          const point: Point = [coords[0], coords[1]];
          const verticalCheckingBounds: PolygonArray = [
            [checkingWidth - delta, checkingHeight],
            [bottomLeft[0], bottomLeft[1]],
          ];
          return isPointInBoxVertical(point, verticalCheckingBounds);
        });

        if (isResourceInVerticalBound) {
          checkingHeight = checkingHeight + delta;
        }
      }

      if (isResourceInHorizontalBound && isResourceInVerticalBound) {
        return foldPoints;
      } else {
        foldPoints = [
          [checkingWidth, bottomLeft[1]],
          [checkingWidth, checkingHeight],
          [bottomLeft[0], checkingHeight],
        ];
      }
    }
  }
}
